import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { toast } from '@/lib/swal';

interface Props {
  inspectionId: string;
  findingId: string;
  canCreate: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDateTime(v: string): string {
  return new Date(v).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getRoleDotClass(roleName: string): string {
  const n = roleName.toLowerCase();
  if (n.includes('admin')) return 'bg-red-500';
  if (n.includes('commercial')) return 'bg-green-600';
  if (n.includes('operations')) return 'bg-orange-500';
  if (n.includes('master')) return 'bg-purple-600';
  if (n.includes('engineer')) return 'bg-blue-500';
  return 'bg-muted-foreground';
}

export function FindingNotesSection({ inspectionId, findingId, canCreate }: Props) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['finding-notes', inspectionId, findingId],
    queryFn: () => inspectionService.getFindingNotes(inspectionId, findingId, { pageSize: 200 }),
    staleTime: 15_000,
  });

  const createMutation = useMutation({
    mutationFn: (content: string) =>
      inspectionService.createFindingNote(inspectionId, findingId, content),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['finding-notes', inspectionId, findingId] });
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to save note')),
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const trimmed = draft.trim();
      if (trimmed && !createMutation.isPending) {
        createMutation.mutate(trimmed);
      }
    }
  };

  const notes = data?.data ?? [];

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faNoteSticky} className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Finding Notes</h2>
        </div>
        {data && (
          <span className="text-xs text-muted-foreground">
            {data.total} note{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="px-5 py-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No notes yet.{canCreate ? ' Use the composer below to add the first note.' : ''}
          </p>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => {
              const dotClass = getRoleDotClass(note.authorRoleName);
              return (
                <div key={note.id} className="flex gap-3">
                  <div
                    className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${dotClass}`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-medium text-foreground">{note.authorName}</span>
                      <span className="text-xs text-muted-foreground">{note.authorRoleName}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                        {fmtDateTime(note.createdAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                      {note.content}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {canCreate && (
          <div className="mt-5 border-t border-border pt-4">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a note… (Enter to save · Shift+Enter for new line)"
              rows={3}
              maxLength={5000}
              disabled={createMutation.isPending}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            />
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {draft.length}/5000 · Enter to save · Shift+Enter for new line
              </p>
              {createMutation.isPending && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-3 w-3 animate-spin text-muted-foreground"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
